import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../../functions/useHandleBlur";
import { useFormState } from "../../../../functions/useFormState";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../../redux/slices/apiUrlSlice";
import { selectDischargeFilter, selectFieldDescriptions } from "../../../../redux/selectors";
import { selectApiStatus } from "../../../../redux/slices/apiStatusSlice"; // Import the selector
import useFieldClass from "../../../../functions/useFieldClass";

const DischargeFilter = ({ zone }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "dischargeFilter";

  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const error = null;

  const { options } = useSelector((state) => state.dropdown);

    /****************Add dropdown options here*************************/

    const manufacturerOptions =
      options?.filter((option) => option.DropdownType === "Filter Manufacturer").sort((a, b) => a.SortOrder - b.SortOrder) || [];
    const typeOptions =
      options?.filter((option) => option.DropdownType === "Discharge Filter Type").sort((a, b) => a.SortOrder - b.SortOrder) || [];
    const modelOptions =
      options?.filter((option) => option.DropdownType === "Discharge Filter Model").sort((a, b) => a.SortOrder - b.SortOrder) || [];
    const filterFlushTypeOptions =
      options?.filter((option) => option.DropdownType === "Filter Flush Type").sort((a, b) => a.SortOrder - b.SortOrder) || [];
    const flushValveTypeOptions =
      options?.filter((option) => option.DropdownType === "Flush Valve Type").sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  const storedJobDetails = useSelector(selectDischargeFilter);
  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});
  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        quantity: jobDetails.Quantity || "",
        manufacturer: jobDetails.Manufacturer || "",
        type: jobDetails.Type || "",
        model: jobDetails.Model || "",
        futureFilters: jobDetails.FutureFilters || "",
        filterMeshSize: jobDetails.FilterMeshSize || "",
        bypass: jobDetails.Bypass || "",
        filterFlushType: jobDetails.FilterFlushType || "",
        flushValveType: jobDetails.FlushValveType || "",
        dischargeFilterComments: jobDetails.DischargeFilterComments || "",
        // ...other fields...
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="filtration-condensed-grid">
      <Row className="mb-3 filtration-field-row">
        <Col>
          <Form.Group as={Row} controlId="quantity">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("Quantity")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Quantity</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Control
                type="text"
                name="quantity"
                value={formData.quantity || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Quantity", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "Quantity",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.quantity}
                onChange={handleChange}
                placeholder="Enter Quantity"
              />
            </Col>
          </Form.Group>
        </Col>

        <Col>
          <Form.Group as={Row} controlId="manufacturer">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("Manufacturer")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Manufacturer</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Select
                name="manufacturer"
                value={formData.manufacturer || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Manufacturer", manufacturerOptions, {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "Manufacturer",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.manufacturer}
                onChange={handleChange}
              >
                <option value="">Select Manufacturer</option>
                {manufacturerOptions.map((option) => (
                  <option key={option.ID} value={option.DisplayText}>
                    {option.DisplayText}
                  </option>
                ))}
                {!isValueInDropdown(
                  formData.manufacturer,
                  manufacturerOptions
                ) &&
                  formData.manufacturer && (
                    <option
                      value={formData.manufacturer}
                      style={{ color: "red" }}
                    >
                      *{formData.manufacturer}
                    </option>
                  )}
              </Form.Select>
            </Col>
          </Form.Group>
        </Col>

        <Col>
          <Form.Group as={Row} controlId="type">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("Type")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Type</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Select
                name="type"
                value={formData.type || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Type", typeOptions, {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "Type",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.type}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                {typeOptions.map((option) => (
                  <option key={option.ID} value={option.DisplayText}>
                    {option.DisplayText}
                  </option>
                ))}
                {!isValueInDropdown(formData.type, typeOptions) &&
                  formData.type && (
                    <option value={formData.type} style={{ color: "red" }}>
                      *{formData.type}
                    </option>
                  )}
              </Form.Select>
            </Col>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3 filtration-field-row">
        <Col>
          <Form.Group as={Row} controlId="model">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("Model")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Model</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Select
                name="model"
                value={formData.model || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Model", modelOptions, {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "Model",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.model}
                onChange={handleChange}
              >
                <option value="">Select Model</option>
                {modelOptions.map((option) => (
                  <option key={option.ID} value={option.DisplayText}>
                    {option.DisplayText}
                  </option>
                ))}
                {!isValueInDropdown(formData.model, modelOptions) &&
                  formData.model && (
                    <option value={formData.model} style={{ color: "red" }}>
                      *{formData.model}
                    </option>
                  )}
              </Form.Select>
            </Col>
          </Form.Group>
        </Col>

        <Col>
          <Form.Group as={Row} controlId="futureFilters">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("FutureFilters")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Future Filters</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Control
                type="text"
                name="futureFilters"
                value={formData.futureFilters || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Future Filters", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "FutureFilters",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.futureFilters}
                onChange={handleChange}
                placeholder="Enter Future Filters"
              />
            </Col>
          </Form.Group>
        </Col>

        <Col>
          <Form.Group as={Row} controlId="filterMeshSize">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("FilterMeshSize")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Filter Mesh Size</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Control
                type="text"
                name="filterMeshSize"
                value={formData.filterMeshSize || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Filter Mesh Size", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "FilterMeshSize",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.filterMeshSize}
                onChange={handleChange}
                placeholder="Enter Filter Mesh Size"
              />
            </Col>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3 filtration-field-row">
        <Col>
          <Form.Group as={Row} controlId="bypass">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("Bypass")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Bypass</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Control
                type="text"
                name="bypass"
                value={formData.bypass || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Bypass", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "Bypass",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.bypass}
                onChange={handleChange}
                placeholder="Enter Bypass"
              />
            </Col>
          </Form.Group>
        </Col>

        <Col>
          <Form.Group as={Row} controlId="filterFlushType">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("FilterFlushType")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Filter Flush Type</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Select
                name="filterFlushType"
                value={formData.filterFlushType || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Filter Flush Type", filterFlushTypeOptions, {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "FilterFlushType",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.filterFlushType}
                onChange={handleChange}
              >
                <option value="">Select Filter Flush Type</option>
                {filterFlushTypeOptions.map((option) => (
                  <option key={option.ID} value={option.DisplayText}>
                    {option.DisplayText}
                  </option>
                ))}
                {!isValueInDropdown(
                  formData.filterFlushType,
                  filterFlushTypeOptions
                ) &&
                  formData.filterFlushType && (
                    <option
                      value={formData.filterFlushType}
                      style={{ color: "red" }}
                    >
                      *{formData.filterFlushType}
                    </option>
                  )}
              </Form.Select>
            </Col>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group as={Row} controlId="flushValveType">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("FlushValveType")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Flush Valve Type</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Select
                name="flushValveType"
                value={formData.flushValveType || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Flush Valve Type", flushValveTypeOptions, {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "FlushValveType",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.flushValveType}
                onChange={handleChange}
              >
                <option value="">Select Flush Valve Type</option>
                {flushValveTypeOptions.map((option) => (
                  <option key={option.ID} value={option.DisplayText}>
                    {option.DisplayText}
                  </option>
                ))}
                {!isValueInDropdown(
                  formData.flushValveType,
                  flushValveTypeOptions
                ) &&
                  formData.flushValveType && (
                    <option
                      value={formData.flushValveType}
                      style={{ color: "red" }}
                    >
                      *{formData.flushValveType}
                    </option>
                  )}
              </Form.Select>
            </Col>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3 filtration-field-row filtration-comments-row">
        <Col>
          <Form.Group controlId="dischargeFilterComments">
            <Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("DischargeFilterComments")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Comments</span>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="dischargeFilterComments"
              value={formData.dischargeFilterComments || ""}
              onFocus={handleFocus}
              onBlur={(e) =>
                handleBlur(e, "Discharge Filter Comments", [], {
                  ID: formData.ID,
                  url: apiUrl,
                  field: "DischargeFilterComments",
                  tableKey,
                })
              }
              disabled={isDisabled || (!roles.Admin && !roles.Apps)}
              className={fieldClasses.dischargeFilterComments}
              onChange={handleChange}
              placeholder="Enter Comments"
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default DischargeFilter;
